import { ajax } from "lib/observable";
import _ from "lodash";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import { PRODUCT } from "constants/action-types";
import {
    Item as ProductSchema,
    Items as ProductsSchema,
    Availability as AvailabilitySchema,
    Inventory as InventorySchema,
    ReplenishmentGroups as ReplenishmentGroupsSchema,
} from "schemas/items";
import siteConfig from "site-config";
import { updateEntities } from "redux-query";
import { normalizeData } from "helpers/data/normalize";
import { stringify } from "querystring";
import { fetchWarehouseAvailabilityForProduct, fetchWarehouseReplenishmentsForProduct } from "actions/products/product";
import { Warehouses as WarehousesSchema } from "schemas/warehouses";
import me from "models/me";
import { filterProductColorVariants, getProductColorRestriction } from "../../queries/items";
import { IStore } from "reducers";
import { getDefaultWarehouse } from "helpers/show-ordered-qty";

// shared among endpoints
export type IUploadResponse = {
    message: string;
    localizedMessage?: string;
    job?: any;
    errors?: string[];
};

export const fetchProductInitEpic = (actions$, store) =>
    actions$
        .ofType(PRODUCT.FETCH.INIT)
        .map(({ payload }) => payload)
        .flatMap(({ id, force, options }) => {
            const { entities } = store.getState();
            const product = entities.items && entities.items[id];
            if (product && !force) {
                return [
                    {
                        options,
                        meta: {
                            product,
                        },
                        payload: id,
                        type: PRODUCT.FETCH.COMPLETED,
                    },
                ];
            } else {
                return [
                    {
                        payload: {
                            id,
                            force,
                            options,
                        },
                        type: PRODUCT.FETCH.START,
                    },
                ];
            }
        });

export const fetchProductEpic = (action$, store) =>
    action$
        .ofType(PRODUCT.FETCH.START)
        .map(({ payload }) => payload)
        .mergeMap(({ id, options }) => {
            const { scope, entities } = store.getState();
            const { effectiveDate, forceViewDisabled, availableFrom, availableUntil } = options;

            const query: any = {
                __effective_date_gte: effectiveDate,
                __availability_start_date_gte: availableFrom,
                __availability_end_date_lte: availableUntil,
                __active_pricesheet: me._activePriceTemplate,
                __fields: "__all",
                __populate: ["__currency_breakdown", "__promotions", "__variant"],
                __view_disabled: true,
                __force_view_disabled: forceViewDisabled || false,
            };

            query.__advanced_pricing_currency_code = scope.brand?.getAdvancedPricingCurrencyCode(scope);

            if (scope.viewAs) {
                query.__company = scope.viewAs._id;
            }

            return ajax(`${siteConfig.api}/api/v3.0/item/${id}?${stringify(query)}`)
                .flatMap(({ response, status, xhr }) => {
                    const data = normalizeData(response, ProductSchema, entities.schemas, scope);
                    const key = Object.keys(data.entities.items)[0];
                    const product = data.entities.items[key];
                    me.nuAnalytics("productImpression", {
                        id: product._id,
                        n: product.name,
                        l: me.nuAnalyticsLocation(),
                        b: scope.brand._id,
                        c: product.category,
                        v: product.color,
                        p: 0,
                        pc: product.formatPriceForAnalytics(scope),
                    });

                    return [
                        updateEntities({
                            items: (currentProducts) => {
                                return {
                                    ...currentProducts,
                                    ...data.entities.items,
                                };
                            },
                        }),
                        {
                            options,
                            meta: {
                                product,
                            },
                            payload: key,
                            type: PRODUCT.FETCH.COMPLETED,
                        },
                    ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: PRODUCT.FETCH.COMPLETED,
                        })
                    )
                );
        });

export const fetchProductVariantsEpic = (action$, store) =>
    action$
        .ofType(PRODUCT.FETCH.COMPLETED)
        .filter(({ error, meta }) => {
            return !error && meta.product;
        })
        .switchMap(({ options, meta: { product } }) => {
            const { scope, entities, catalog } = store.getState();
            const { effectiveDate, forceViewDisabled } = options;
            const catalogScoped = !!window.location?.pathname?.indexOf("/linesheet/");

            const startDate =
                catalogScoped && catalog.result.length
                    ? catalog.entities.catalogs[catalog.result]?.get("delivery_window_start")
                    : undefined;

            const endDate =
                catalogScoped && catalog.result.length
                    ? catalog.entities.catalogs[catalog.result]?.get("delivery_window_end")
                    : undefined;

            const query: any = {
                __effective_date_gte: effectiveDate,
                __availability_start_date_gte: startDate,
                __availability_end_date_lte: endDate,
                __force_view_disabled: forceViewDisabled || false,
                __active_pricesheet: me._activePriceTemplate,
                __fields: "__all",
                __limit: 1000,
                __populate: ["__currency_breakdown", "__promotions", "__variant"],
                style_number: product.get("style_number"),
            };

            query.__advanced_pricing_currency_code = scope.brand?.getAdvancedPricingCurrencyCode(scope);

            if (scope.viewAs) {
                query.__company = scope.viewAs._id;
            }

            return ajax(`${siteConfig.api}/api/v3.0/items?${stringify(query)}`)
                .flatMap(({ response, status, xhr }) => {
                    response = filterProductColorVariants(
                        response.data || response,
                        getProductColorRestriction(product)
                    );
                    response.sort((a, b) => {
                        if (a.color < b.color) {
                            return -1;
                        }
                        if (a.color > b.color) {
                            return 1;
                        }
                        return 0;
                    });
                    const data = normalizeData(response, ProductsSchema, entities.schemas, scope);
                    return [
                        updateEntities({
                            items: (currentProducts) => {
                                return {
                                    ...currentProducts,
                                    ...data.entities.items,
                                };
                            },
                        }),
                        {
                            payload: data.result,
                            type: PRODUCT.VARIANTS.FETCH.COMPLETED,
                        },
                    ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: PRODUCT.VARIANTS.FETCH.COMPLETED,
                        })
                    )
                );
        });

export const fetchWarehouseInventoryForProductEpic = (action$, store) =>
    action$
        .ofType(PRODUCT.WAREHOUSE.INVENTORY.FETCH.START)
        .mergeMap(({ payload: { productId, warehouseId, timestamp, fromRef, forRef } }) => {
            const { scope, entities, catalog, cart } = store.getState();
            const catalogScoped = !!window.location?.pathname?.indexOf("/linesheet/");
            const cartScoped = !!window.location?.pathname?.indexOf("/cart");
            const query: any = _.pickBy({ __date: timestamp.valueOf(), fromRef, forRef });

            if (scope.viewAs) {
                query.__company = scope.viewAs._id;
            }

            const effectiveDate =
                catalogScoped && catalog.result.length
                    ? catalog.entities.catalogs[catalog.result]?.get("delivery_window_start")
                    : undefined;

            if (cartScoped) {
                query.forceViewDisabled = true;
            }

            if (effectiveDate) {
                query.effectiveDateMs = effectiveDate;
            }

            return ajax(
                `${siteConfig.api}/api/v3.0/item/${productId}/warehouse/${warehouseId}/available-inventory?${stringify(
                    query
                )}`
            )
                .flatMap(({ response, status, xhr }) => {
                    const data = normalizeData(response, InventorySchema, entities.schemas, scope);

                    return [
                        updateEntities({
                            inventory: (currentInventory) => {
                                return {
                                    ...currentInventory,
                                    ...data.entities.inventory,
                                };
                            },
                        }),
                        {
                            payload: {
                                arrivingInventory: data.result,
                            },
                            type: PRODUCT.WAREHOUSE.INVENTORY.FETCH.COMPLETED,
                        },
                    ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: PRODUCT.WAREHOUSE.INVENTORY.FETCH.COMPLETED,
                        })
                    )
                );
        });

export const fetchProductWarehousesEpic = (action$, store) =>
    action$.ofType(PRODUCT.WAREHOUSES.FETCH.START).switchMap(({ payload: { productId, params } }) => {
        const { scope, entities, cart, catalog } = store.getState() as IStore;

        const query: any = {};

        if (scope.viewAs) {
            query.__company = scope.viewAs._id;
        }
        const catalogScoped = !!window.location?.pathname?.indexOf("/linesheet/");
        const cartScoped = !!window.location?.pathname?.indexOf("/cart");

        const effectiveDate =
            catalogScoped && catalog.result.length
                ? catalog.entities.catalogs[catalog.result]?.get("delivery_window_start")
                : undefined;

        if (effectiveDate) {
            query.effectiveDateMs = effectiveDate;
        }

        if (cartScoped) {
            // Product is already added to cart, so skipping visibility part
            query.forceViewDisabled = true;
        }

        return ajax(`${siteConfig.api}/api/v3.0/item/${productId}/warehouses?${stringify(query)}`)
            .flatMap(({ response, status, xhr }) => {
                const data = normalizeData(response, WarehousesSchema, entities.schemas, scope);
                const populateReplenishments = _.get(params, "populateReplenishments", true);

                const warehouseId = scope.brand.hasPreference("show_ordered_qty")
                    ? getDefaultWarehouse({ cart, productId, warehouses: data.result, warehouse: params?.warehouse })
                    : data.result[0];

                const actions = [
                    updateEntities({
                        warehouses: (currentWarehouses) => {
                            return {
                                ...currentWarehouses,
                                ...data.entities.warehouses,
                            };
                        },
                    }),
                    {
                        payload: {
                            warehouseIds: data.result,
                        },
                        type: PRODUCT.WAREHOUSES.FETCH.COMPLETED,
                    },
                ];

                if (!scope.brand.hasPreference("set_warehouse_for_linesheets") || !warehouseId) {
                    actions.push(fetchWarehouseAvailabilityForProduct(productId, warehouseId));
                }
                if (populateReplenishments && warehouseId) {
                    actions.push(fetchWarehouseReplenishmentsForProduct(productId, warehouseId));
                }

                return actions;
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of({
                        error: true,
                        payload: error,
                        type: PRODUCT.WAREHOUSES.FETCH.COMPLETED,
                    })
                )
            );
    });

export const fetchProductWarehouseAvailabilityEpic = (action$, store) =>
    action$.ofType(PRODUCT.WAREHOUSE.AVAILABILITY.FETCH.START).switchMap(({ payload: { productId, warehouseId } }) => {
        const { scope, entities, catalog } = store.getState();
        const catalogScoped = !!window.location?.pathname?.indexOf("/linesheet/");
        const cartScoped = !!window.location?.pathname?.indexOf("/cart");

        const effectiveDate =
            catalogScoped && catalog.result.length
                ? catalog.entities.catalogs[catalog.result]?.get("delivery_window_start")
                : null;

        const query: any = {};
        if (effectiveDate) {
            query.effectiveDateMs = effectiveDate;
        }

        if (cartScoped) {
            // Product is already added to cart, so skipping visibility part
            query.forceViewDisabled = true;
        }

        if (scope.viewAs) {
            query.__company = scope.viewAs._id;
        }

        const endpoint = warehouseId
            ? `${siteConfig.api}/api/v3.0/item/${productId}/warehouse/${warehouseId}/availability?${stringify(query)}`
            : `${siteConfig.api}/api/v3.0/item/${productId}/availability?${stringify(query)}`;

        return ajax(endpoint)
            .flatMap(({ response, status, xhr }) => {
                const data = normalizeData(response, AvailabilitySchema, entities.schemas, scope);

                return [
                    updateEntities({
                        itemAvailabilities: (currentAvailabilities) => {
                            return {
                                ...currentAvailabilities,
                                ...data.entities.itemAvailabilities,
                            };
                        },
                        itemDeliveryWindows: (currentADeliveryWindows) => {
                            return {
                                ...currentADeliveryWindows,
                                ...data.entities.itemDeliveryWindows,
                            };
                        },
                    }),
                    {
                        payload: {
                            availabilityId: data.result,
                        },
                        type: PRODUCT.WAREHOUSE.AVAILABILITY.FETCH.COMPLETED,
                    },
                ];
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of({
                        error: true,
                        payload: error,
                        type: PRODUCT.WAREHOUSE.AVAILABILITY.FETCH.COMPLETED,
                    })
                )
            );
    });

export const fetchProductWarehouseReplenishmentsEpic = (action$, store) =>
    action$
        .ofType(PRODUCT.WAREHOUSE.REPLENISHMENTS.FETCH.START)
        .switchMap(({ payload: { productId, warehouseId } }) => {
            const { scope, entities, catalog, cart } = store.getState();
            const catalogScoped = !!window.location?.pathname?.indexOf("/linesheet/");
            const cartScoped = !!window.location?.pathname?.indexOf("/cart");

            const query: any = {};
            const effectiveDate =
                catalogScoped && catalog.result.length
                    ? catalog.entities.catalogs[catalog.result]?.get("delivery_window_start")
                    : undefined;

            if (effectiveDate) {
                query.effectiveDateMs = effectiveDate;
            }

            if (cartScoped) {
                query.forceViewDisabled = true;
            }

            if (scope.viewAs) {
                query.__company = scope.viewAs._id;
            }

            const endpoint = `${
                siteConfig.api
            }/api/v3.0/item/${productId}/warehouse/${warehouseId}/replenishments?${stringify(query)}`;

            return ajax(endpoint)
                .flatMap(({ response, status, xhr }) => {
                    const data = normalizeData(response, ReplenishmentGroupsSchema, entities.schemas, scope);

                    return [
                        updateEntities({
                            replenishmentGroups: (currentReplenishments) => {
                                return {
                                    ...currentReplenishments,
                                    ...data.entities.replenishmentGroups,
                                };
                            },
                        }),
                        {
                            payload: {
                                replenishmentGroupIds: data.result,
                            },
                            type: PRODUCT.WAREHOUSE.REPLENISHMENTS.FETCH.COMPLETED,
                        },
                    ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: PRODUCT.WAREHOUSE.REPLENISHMENTS.FETCH.COMPLETED,
                        })
                    )
                );
        });
